import { SidebarTab } from '../../types';
import { filesystemService } from '../utils/filesystem';
import { makeAutoObservable } from 'mobx';
import { projectService } from '../services/ProjectService';
import { pyodideService } from '../services/PyodideService';

class ProjectStore {
    projectId: string | null = null;
    projectName: string | null = null;
    loading = false;
    selectedFile: string | null = null;
    isGenerating = false;

    constructor() {
        makeAutoObservable(this);
    }

    setProjectId(id: string | null) {
        this.projectId = id;
    }

    setProjectName(name: string | null) {
        this.projectName = name;
    }

    setLoading(loading: boolean) {
        this.loading = loading;
    }

    setSelectedFile(file: string | null) {
        this.selectedFile = file;
    }

    setIsGenerating(isGenerating: boolean) {
        this.isGenerating = isGenerating;
    }

    async createNewProject(updateLogs?: (message: string) => void, setActiveTab?: (tab: SidebarTab) => void, type: string = 'blank') {
        this.setLoading(true);
        try {
            if (updateLogs) updateLogs("Creating new project...");

            const project = await projectService.createProject(undefined, type);

            this.setProjectId(project.id);
            this.setProjectName(project.name);

            if (updateLogs) updateLogs(`Project created: ${project.name}`);
            if (setActiveTab) setActiveTab(SidebarTab.Editor);

            return { projectId: project.id, projectName: project.name };
        } catch (e) {
            if (e instanceof Error) {
                if (updateLogs) updateLogs(`Error creating project: ${e.message}`);
                console.error(e);
            }
            throw e;
        } finally {
            this.setLoading(false);
        }
    }

    async loadProject(projectId: string, updateLogs?: (message: string) => void, setActiveTab?: (tab: SidebarTab) => void) {
        try {
            await pyodideService.shutdown();
        } catch (e) {
            console.warn('Failed to shutdown Pyodide before loading project', e);
        }

        filesystemService.clearPendingFiles();

        this.setLoading(true);
        try {
            if (updateLogs) updateLogs(`Loading project ${projectId}...`);

            const project = await projectService.getProject(projectId);

            this.setProjectId(projectId);
            this.setProjectName(project.name);

            if (updateLogs) updateLogs(`Project loaded: ${project.name}`);
            if (setActiveTab) setActiveTab(SidebarTab.Editor);

            return project;
        } catch (e) {
            if (e instanceof Error) {
                if (updateLogs) updateLogs(`Error loading project: ${e.message}`);
                console.error(e);
            }
            this.setProjectId(null);
            this.setProjectName(null);
            throw e;
        } finally {
            this.setLoading(false);
        }
    }

    async getProjectById(projectId: string) {
        try {
            return await projectService.getProject(projectId);
        } catch (e) {
            console.error(`Error fetching project ${projectId}:`, e);
            throw e;
        }
    }

    returnToProjects() {
        this.setProjectId(null);
        this.setProjectName(null);
        this.setSelectedFile(null);

        pyodideService.shutdown().catch(console.error);
        filesystemService.clearPendingFiles();
        projectService.setCurrentProjectId(null);
    }
}

export const projectStore = new ProjectStore(); 